import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { Commands, People, Search } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { ContextMenuService } from '@shared/components';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { isOpenUrlCommand } from '@shared/utils';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { ResourceViewItemComponent } from '../../view-item/resource-view-item.component';
import { Action, PageType } from '../../../results/models/view-filters';
import { PeopleCommandBuilder, PeopleCommandBuilderParams } from '../people-command-builder.service';
import { KeyValueType, PeopleContextMenuHelper } from '../people-context-menu-helper';
import { DisplayItemData } from '../../../results/models/results-types';
import { getPrimaryActionClick, isEnabledSummary, isPreviewClickAction } from '../../../results/utils/results.util';
import { PeopleViewItemService } from '../people-view-item.helper';
import { RelevantPeopleViewType } from '../../relevant-people/models/relevant-people-views.type';
import { BehaviorSubject, Subscription, filter } from 'rxjs';
import { PeopleService } from '../../../preview/people-preview/services/people.service';
import { ContactLinkViewModel } from '../../../preview/people-preview/models/contact-link-view.model';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { WorkspacesService } from 'src/app/bar/services';
import { getProfileIcon } from '@shared/utils/set-icon.util';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';
import { isExtension } from '@local/common-web';

@Component({
  selector: 'people-view-item',
  templateUrl: './people-view-item.component.html',
  styleUrls: ['./people-view-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleViewItemComponent extends ResourceViewItemComponent implements OnInit, OnDestroy {
  contactLinks: Record<string, ContactLinkViewModel> = {};
  peopleInfo: People.PersonContext;
  error$ = new BehaviorSubject(false);
  isExtension = isExtension();
  private subscription: Subscription;
  private _viewMode: RelevantPeopleViewType = 'line';

  @Input() set viewMode(val: RelevantPeopleViewType) {
    this._viewMode = val;
  }

  get viewMode(): RelevantPeopleViewType {
    return this._viewMode;
  }

  @Input() set model(value: Search.ResultResourceItem) {
    this.prevModel = this._model;
    this._model = value;
    this.unregisterFromCommandBar();
    this.onChanges('model');
  }
  @Input() displayOneCenterLine = false;

  get model() {
    return super.model;
  }

  get name(): PageType {
    return 'people';
  }

  get allowPageType() {
    return ['search', 'static-collection', 'favorites', 'people'].includes(this.pageType);
  }

  @HostBinding('attr.viewMode') get itemViewMode() {
    return this.viewMode;
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    peopleCommandBuilder: PeopleCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    protected keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    private peopleBuilder: PeopleCommandBuilder,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    public peopleService: PeopleService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      peopleCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.viewMode === 'gallery') {
      this.initPerson();
    }
    if (this.model.showResultSections) {
      this.checkShowResultSections();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscription?.unsubscribe();
  }

  private checkShowResultSections() {
    this.setShowResultSections(this.model.showResultSections);
    this.setShowResultSections(this.showResultSectionsDefault, false);
  }

  initIcons() {
    super.initIcons();
    if (this.iconModel?.value === 'icon-user') {
      this.iconModel = { type: 'img', value: getProfileIcon() };
    }
  }

  initPerson() {
    this.error$.next(false);
    this.subscription?.unsubscribe();
    this.peopleInfo = null;

    this.peopleService
      .getPerson(this.model?.resource?.externalId)
      .then((val) => {
        this.subscription = val.pipe(filter((val) => !!val)).subscribe(
          async (res) => {
            this.peopleInfo = res;
            if (this.peopleInfo?.details.contactLinks) {
              await this.buildContactLinks();
            }
            this.cdr.markForCheck();
          },
          () => {
            this.error$.next(true);
          }
        );
      })
      .catch(() => {
        this.error$.next(true);
      });
  }

  private async buildContactLinks() {
    this.contactLinks = await this.peopleService.buildContactLinks(this.peopleInfo.details, this.searchId);
    this.cdr.markForCheck();
  }

  async setShortcutsHandlers() {
    await super.setShortcutsHandlers();
    PeopleViewItemService.setShortcutsHandlers(this.keyboardService, this.shortcutsHandlers, this, this.model);
  }

  async onInvoke(invokeData: InvokeCommand): Promise<void> {
    invokeData.eventInfo = { resources: [{ position: this.resultsIndex }] };
    const copyValue: string = (<Commands.CopyClipboard>invokeData.command)?.value;
    if (
      isOpenUrlCommand(invokeData.command) &&
      !invokeData?.trigger?.includes('context_menu') &&
      getPrimaryActionClick((this.model as DisplayItemData)?.action) === 'preview'
    ) {
      invokeData.command = this.buildPreviewCommand();
    }
    if (copyValue) {
      let subType;
      if (copyValue === this.model.resource?.traits?.email) {
        subType = 'copy-email';
      } else if (copyValue.includes(this.model.resource.traits?.phone?.number)) {
        subType = 'copy-phone';
      }

      invokeData = <PeopleInvokeCommand>{ ...invokeData, subType: subType };
      if (invokeData.trigger === 'mouse_click') {
        const ev: Partial<EventInfo> = {};
        const key = `people_view.${subType === 'copy-email' ? 'copy_email' : 'copy_phone'}`;
        this.sendEvent(ev, key);
      }
    }

    this.invoke.emit({ ...invokeData, invokerItem: this.model });
  }

  protected async buildPresentableCommandsParams(): Promise<PeopleCommandBuilderParams> {
    const itemsRequest = await super.buildPresentableCommandsParams();
    let copyEmail: KeyValueType;
    let copyPhone: KeyValueType;
    if (this.model.resource?.traits?.email) {
      copyEmail = { key: 'email', value: this.model.resource.traits.email };
    }
    if (this.model.resource.traits?.phone) {
      const phone = this.model.resource.traits?.phone;
      const phoneNumber = phone?.code ? `+${phone.code}-${phone.number}` : phone.number;
      copyPhone = { key: 'phoneNumber', value: phoneNumber };
    }
    return { ...itemsRequest, copyEmail, copyPhone };
  }

  protected sendEvent(ev: Partial<EventInfo> = {}, key?: string) {
    ev.resources = this.getEventResources('people');
    ev.location = { title: 'people' };
    super.sendEvent(ev, key ? key : 'people_view.open');
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new PeopleContextMenuHelper(
      this.peopleBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
  }

  galleryInvoke($event) {
    switch ($event.type) {
      case 'contextmenu':
        this.openContextMenu($event.event, 'actions');
        break;
      case 'preview':
        this.onPreviewClick();
        if (this.isClickable) {
          $event.event.stopPropagation();
        }
        break;
      case 'checkbox':
        this.changeSelection.emit($event.event);
        break;
      case 'title':
        this.onInvoke({ command: this.model?.view?.title?.onClick, source: 'title' });
        break;
    }
  }

  isPreviewClickAction(action: Action) {
    return isPreviewClickAction(action);
  }

  isAvailableSummary(action: Action) {
    return isEnabledSummary(action);
  }

  onAvatarClick() {
    this.onInvoke({
      source: 'avatar',
      trigger: 'mouse_click',
      command: { type: 'open-url' },
      context: {
        appId: this.model.resource.appId,
      } as Commands.Context,
    });
  }
}

export interface PeopleInvokeCommand extends InvokeCommand {
  subType?: subType;
}

export type subType = 'copy-email' | 'copy-phone';
